import React, { Component } from 'react';
import { makeObservable, action, observable } from 'mobx';
import { observer } from "mobx-react";
import DatePicker from "react-datepicker";
import ru from "date-fns/locale/ru";
import 'Styles/commonComponents/DateTimePicker.scss';
import IconsContainer from 'Utils/IconsContainer';
import { ShowModal, ShowAlert } from "Utils/CommonFunctions";
import HelperDatePickerRange from "Utils/HelperDatePickerRange";

/**
 * @typedef {object} DateTimePickerRenderStore
 * @property {boolean} dateDropDownShow
 * @property {boolean} timeDropDownShow
 * @property {number} hours
 * @property {number} minutes
 * @property {boolean} [dateIsSelected]
 * @property {Date} [date]
 * @property {boolean} [hoursClicked]
 * @property {boolean} [minutesClicked]
 */

/**
 * @typedef {object} DateTimePickerProps
 * @property {string} [className]
 * @property {() => Date | null} [getValue]
 * @property {(date: Date | null) => void} [setValue]
 * @property {() => Date} [getMinDate]
 * @property {() => Date} [getMaxDate]
 * @property {() => DateTimePickerRenderStore | null} getRenderStore
 * @property {(store: DateTimePickerRenderStore) => void} setRenderStore
 * @property {boolean} isTop
 * @property {boolean} [isDefaultTimeEndOfDay]
 * @property {boolean} [readOnly]
 * @property {boolean} [canClear]
 * @property {string} [emptyText]
 * @property {boolean} [timeVisible]
 * @property {import('csstype').Property.TextWrap} [textWrap]
 * @extends {Component<DateTimePickerProps>}
 */
class DateTimePicker extends Component {

    hours = [];
    minutes = [];
    emptyTextDefault = "дата";

    /**
     * @param {DateTimePickerProps} props
     */
    constructor(props) {
        super(props);

        for (let i = 0; i <= 23; i++) {
            this.hours.push(i);
        }
        for (let i = 0; i <= 59; i++) {
            this.minutes.push(i);
        }
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = action((event) => {
        if (this.htmlElement?.contains(event.target) == true)
            return;

        this.GetRenderStore().dateDropDownShow = false;
        if (this.IsStoreValueEmpty() == false && this.TimeValuesAnyClicked()) {
            this.TimeDropDownSaveOnСlick();
        }
        else {
            this.GetRenderStore().timeDropDownShow = false;
        }
    })

    ReadOnly() {
        return this.props.readOnly == true;
    }

    IsCanClear() {
        return this.props.canClear != false && this.props.readOnly != true;
    }

    IsTimeVisible() {
        return this.props.timeVisible != false;
    }

    IsDefaultTimeEndOfDay() {
        return this.props.isDefaultTimeEndOfDay == true;
    }

    DefaultDateValue() {
        let dtNow = new Date();
        let dt = new Date(dtNow.getFullYear(), dtNow.getMonth(), dtNow.getDate(), 0, 0, 0, 0);
        if (this.IsDefaultTimeEndOfDay()) {
            dt.setDate(dt.getDate() + 1);
            dt.setMilliseconds(-1);
        }
        return dt;
    }
    DefaultTimeValue() {
        let dtCurrent = this.GetValueFromStore();
        let dt = new Date(dtCurrent.getFullYear(), dtCurrent.getMonth(), dtCurrent.getDate(), 0, 0, 0, 0);
        if (this.IsDefaultTimeEndOfDay()) {
            dt.setDate(dt.getDate() + 1);
            dt.setMilliseconds(-1);
        }
        return dt;
    }

    MinDate() {
        return this.props.getMinDate ? this.props.getMinDate() : HelperDatePickerRange.GetMinDateFrom();
    }
    MaxDate() {
        return this.props.getMaxDate ? this.props.getMaxDate() : HelperDatePickerRange.GetMaxDateToExistedValues();
    }

    /** @param {Date} dt */
    CorrectDateByMinMax(dt) {
        if (this.ReadOnly())
            return dt;

        let ret = dt.getTime() > this.MaxDate().getTime() ? this.MaxDate() : dt.getTime() < this.MinDate().getTime() ? this.MinDate() : dt;
        return ret;
    }

    IsStoreValueEmpty() {
        return this.props.getValue() == null || this.props.getValue() == undefined;
    }
    GetValueFromStore() {
        let dt = this.props.getValue();
        if (this.ReadOnly())
            return dt;

        if (dt != null && (dt instanceof Date) == false) {
            dt = this.DefaultDateValue(); // на случай ошибок. убрать после рефакторинга всего front
        }
        else if (dt == null || dt.getFullYear == null) {
            dt = this.DefaultDateValue();
        }
        let ret = this.CorrectDateByMinMax(dt);

        return ret;
    }
    SetValueToStore = action((val) => {
        this.props.setValue(val);
    })

    GetRenderStore() {
        /** @type {DateTimePickerRenderStore} */
        let valDefault = {
            dateDropDownShow: false,
            timeDropDownShow: false,
            hours: null,
            minutes: null,
            timeIsSelectedByUser: false
        };
        let ret = this.props.getRenderStore();
        if (ret == null || Object.keys(ret).length == 0) {
            action(() => this.props.setRenderStore(valDefault))();
            return valDefault;
        }
        return ret;
    }

    OnClear() {
        this.SetValueToStore(null);
    }

    DateDropDownOpenOnСlick = action(() => {
        if (this.GetRenderStore().dateDropDownShow)
            return;
        this.GetRenderStore().dateIsSelected = this.IsStoreValueEmpty() == false;
        this.GetRenderStore().date = new Date(this.GetValueFromStore().getTime());
        this.GetRenderStore().dateDropDownShow = true;
        this.GetRenderStore().timeDropDownShow = false;
    })

    TimeDropDownOpenOnСlick = action(() => {
        if (this.GetRenderStore().timeDropDownShow)
            return;
        if (this.GetRenderStore().timeIsSelectedByUser) {
            this.GetRenderStore().hours = this.GetValueFromStore().getHours();
            this.GetRenderStore().minutes = this.GetValueFromStore().getMinutes();
        }
        this.GetRenderStore().dateDropDownShow = false;
        this.GetRenderStore().timeDropDownShow = true;
        this.GetRenderStore().hoursClicked = false;
        this.GetRenderStore().minutesClicked = false;
    })

    DateDropDownValueSelect = action((value) => {
        this.GetRenderStore().date = value;
        this.GetRenderStore().dateIsSelected = true;
        this.DateDropDownSaveOnСlick();
    });

    TimeHoursDropDownValueSelect = action((value) => {
        this.GetRenderStore().hours = value;
        this.GetRenderStore().hoursClicked = true;
        this.TimeAnyDropDownValueSelect();
    });
    TimeMinutesDropDownValueSelect = action((value) => {
        this.GetRenderStore().minutes = value;
        this.GetRenderStore().minutesClicked = true;
        this.TimeAnyDropDownValueSelect();
    });

    TimeValuesAllClicked() {
        return this.GetRenderStore().hoursClicked && this.GetRenderStore().minutesClicked;
    }
    TimeValuesAnyClicked() {
        return this.GetRenderStore().hoursClicked || this.GetRenderStore().minutesClicked;
    }

    TimeAnyDropDownValueSelect() {
        if (this.TimeValuesAllClicked()) {
            this.TimeDropDownSaveOnСlick();
        }
    }

    DropDownsClose = action(() => {
        this.GetRenderStore().dateDropDownShow = false;
        this.GetRenderStore().timeDropDownShow = false;
    })

    DateDropDownSaveOnСlick = action(() => {
        let dtNew = this.GetRenderStore().date;
        let dtDef = this.DefaultDateValue();
        let dtOld = this.IsStoreValueEmpty() ? dtDef : this.GetValueFromStore();
        let dt = this.CorrectDateByMinMax(new Date(dtNew.getFullYear(), dtNew.getMonth(), dtNew.getDate(), dtOld.getHours(), dtOld.getMinutes(), dtOld.getSeconds(), dtOld.getMilliseconds()));

        this.SetValueToStore(dt);
        this.DropDownsClose();
    })

    TimeDropDownSaveOnСlick = action(() => {
        let hoursNew = this.GetRenderStore().hours;
        let minutesNew = this.GetRenderStore().minutes;

        if (hoursNew == null || minutesNew == null) {
            this.DropDownsClose();
            return;
        }

        let dtOld = this.GetValueFromStore();
        let dtDef = this.DefaultTimeValue();
        let dt = new Date(dtOld.getFullYear(), dtOld.getMonth(), dtOld.getDate(), hoursNew, minutesNew, dtDef.getSeconds(), dtDef.getMilliseconds());

        let dtNewRoundedToMinutes = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), dt.getHours(), dt.getMinutes(), 0, 0);
        let dtTmp = this.CorrectDateByMinMax(dt);
        let dtNewCorrectedMinMaxRoundedToMinutes = new Date(dtTmp.getFullYear(), dtTmp.getMonth(), dtTmp.getDate(), dtTmp.getHours(), dtTmp.getMinutes(), 0, 0);
        if (dtNewRoundedToMinutes.getTime() != dtNewCorrectedMinMaxRoundedToMinutes.getTime()) {
            ShowAlert('error', 'Выбрано невалидное время');
            return;
        }

        this.SetValueToStore(dt);
        this.DropDownsClose();
        this.GetRenderStore().timeIsSelectedByUser = true;
    })

    DateClearOnСlick = action(() => {
        this.SetValueToStore(null); // отдельно от времени не очищаем, т.к. для пользователя это будет выглядеть запутано
        this.DropDownsClose();
        this.DateOrTimeClearAfter();
    })

    TimeClearOnСlick = action(() => {
        let dtOld = this.GetValueFromStore();
        let dtDef = this.DefaultDateValue();
        let dt = this.CorrectDateByMinMax(new Date(dtOld.getFullYear(), dtOld.getMonth(), dtOld.getDate(), dtDef.getHours(), dtDef.getMinutes(), dtDef.getSeconds(), dtDef.getMilliseconds()));

        this.SetValueToStore(dt);
        this.DropDownsClose();
        this.DateOrTimeClearAfter();
    })

    DateOrTimeClearAfter() {
        this.GetRenderStore().timeIsSelectedByUser = false;
        this.GetRenderStore().hours = null;
        this.GetRenderStore().minutes = null;
    }

    IsDatePartEmpty() {
        return this.IsStoreValueEmpty();
    }

    IsDatePartEqualDefault() {
        let dtDef = this.CorrectDateByMinMax(this.DefaultDateValue());
        let dt = this.GetValueFromStore();
        return this.IsDatesPartEqual(dt, dtDef);
    }

    IsDatesPartEqual(dt1, dt2) {
        return (
            dt1.getDate() == dt2.getDate()
            &&
            dt1.getMonth() == dt2.getMonth()
            &&
            dt1.getFullYear() == dt2.getFullYear()
        );
    }

    IsTimePartEmpty() {
        return (
            this.IsStoreValueEmpty()
            ||
            (this.ReadOnly() == false && this.GetRenderStore().timeIsSelectedByUser == false)
        );
    }

    IsTimePartEqualDefault() {
        let dtDef = this.CorrectDateByMinMax(this.DefaultTimeValue());
        let dt = this.GetValueFromStore();
        return this.IsTimesPartEqual(dt, dtDef);
    }

    IsTimesPartEqual(dt1, dt2) {
        return (
            dt1.getHours() == dt2.getHours()
            &&
            dt1.getMinutes() == dt2.getMinutes()
        );
    }

    IsCanClearDate() {
        return (
            this.IsCanClear()
            &&
            this.IsStoreValueEmpty() == false
        );
    }

    IsCanClearTime() {
        return (
            this.IsCanClear()
            &&
            this.GetRenderStore().timeIsSelectedByUser == true
        );
    }

    GetHoursMinutesEnabled() {
        let dt = this.GetValueFromStore();
        let hoursSelected = this.GetRenderStore().timeDropDownShow ? this.GetRenderStore().hours : dt.getHours();
        let minTime = this.IsDatesPartEqual(this.MinDate(), dt) ? this.MinDate() : new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0);
        let maxTime = this.IsDatesPartEqual(this.MaxDate(), dt) ? this.MaxDate() : new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 23, 59);
        let hours = this.hours.filter(it => minTime.getHours() <= it && it <= maxTime.getHours());
        let minutes = (minTime.getHours() < hoursSelected && hoursSelected < maxTime.getHours())
            ? this.minutes
            : (minTime.getHours() == hoursSelected && maxTime.getHours() == hoursSelected)
                ? this.minutes.filter(it => it >= minTime.getMinutes() && it <= maxTime.getMinutes())
                : (minTime.getHours() == hoursSelected)
                    ? this.minutes.filter(it => it >= minTime.getMinutes())
                    : (maxTime.getHours() == hoursSelected)
                        ? this.minutes.filter(it => it <= maxTime.getMinutes())
                        : []
            ;
        return { hours, minutes };
    }

    render() {
        let dt = this.GetValueFromStore();

        let dateString = this.IsDatePartEmpty() ? "дд.мм.гггг" : `${dt.getDate().toString().padStart(2, "0")}.${(dt.getMonth() + 1).toString().padStart(2, "0")}.${dt.getFullYear()}`;
        let timeString = this.IsTimePartEmpty() ? "чч:мм" : `${dt.getHours().toString().padStart(2, "0")}:${dt.getMinutes().toString().padStart(2, "0")}`;
        let dateTimeString = dateString + (this.IsTimeVisible() == false ? "" : ` ${timeString}`);
        let emptyText = this.props.emptyText ?? this.emptyTextDefault;
        let hoursEnabled = [];
        let minutesEnabled = [];
        if (!this.IsStoreValueEmpty()) {
            let tmp = this.GetHoursMinutesEnabled();
            hoursEnabled = tmp.hours;
            minutesEnabled = tmp.minutes;
        }

        return (
            <div
                className={`DateTimePicker ${this.ReadOnly() ? "disabled-no-opacity" : ""} ${this.props.className}`}
                ref={node => this.htmlElement = node}
            >
                <div className="values-controls-container">
                    {
                        this.ReadOnly()
                            ? (
                                <span className="read-only-value">
                                    {this.IsStoreValueEmpty() ? emptyText : dateTimeString}
                                </span>
                            )
                            : [
                                (
                                    <div key='date' className="date-container">
                                        <div
                                            className="date-value"
                                            onClick={e => this.DateDropDownOpenOnСlick(e)}
                                        >
                                            {dateString}
                                        </div>
                                        {this.IsCanClearDate() == false ? null : (
                                            <div
                                                className="date-clear-icon"
                                                onClick={() => this.DateClearOnСlick()}
                                            >
                                                {IconsContainer.Get("ci-cross")}
                                            </div>
                                        )}
                                        <div
                                            className={"date-dropdown-container " + (this.props.isTop ? "date-dropdown-container-top" : "date-dropdown-container-bottom")}
                                            style={{ display: this.GetRenderStore().dateDropDownShow ? null : "none" }}
                                        >
                                            <div className="DatePicker-container">
                                                <DatePicker
                                                    selected={this.GetRenderStore().date}
                                                    onSelect={value => this.DateDropDownValueSelect(value)}
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                    inline
                                                    locale={ru}
                                                    minDate={this.MinDate()}
                                                    maxDate={this.MaxDate()}
                                                />
                                            </div>
                                            <div className="date-dropdown-container-buttons" style={{ display: "none" }}>
                                                <button
                                                    className={`date-button-clear ${this.IsCanClearDate() ? "" : "disabled"}`}
                                                    onClick={() => this.DateClearOnСlick()}
                                                >Удалить</button>
                                                <button
                                                    className={`date-button-save blue-button ${this.GetRenderStore().dateIsSelected ? "" : "disabled"}`}
                                                    onClick={() => this.DateDropDownSaveOnСlick()}
                                                >Выбрать</button>
                                            </div>
                                        </div>
                                    </div>
                                ),
                                this.IsTimeVisible() == false ? null : (
                                    <div key='time' className={`time-container ${this.IsStoreValueEmpty() ? "disabled" : ""}`}>
                                        <div
                                            className="time-value"
                                            onClick={_ => this.TimeDropDownOpenOnСlick()}
                                        >
                                            {timeString}
                                        </div>
                                        {this.IsCanClearTime() == false ? null : (
                                            <div
                                                className="time-clear-icon"
                                                onClick={() => this.TimeClearOnСlick()}
                                            >{IconsContainer.Get("ci-cross")}
                                            </div>
                                        )}
                                        <div
                                            className={"time-dropdown-container " + (this.props.isTop ? "time-dropdown-container-top" : "time-dropdown-container-bottom")}
                                            style={{ display: this.GetRenderStore().timeDropDownShow ? null : "none" }}
                                        >
                                            <div className="time-items-container">
                                                <div className="hours-items scroll-hidden">
                                                    {this.hours.map(it => (
                                                        <div
                                                            key={it}
                                                            className={`hour-value-item hour-value-item-${it} ${(this.GetRenderStore().hours == it ? "time-value-item-selected" : "")} ${hoursEnabled.includes(it) == false ? "disabled" : ""}`}
                                                            onClick={_ => this.TimeHoursDropDownValueSelect(it)}
                                                        >
                                                            {it.toString().padStart(2, "0")}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="minutes-items scroll-hidden">
                                                    {this.minutes.map(it => (
                                                        <div
                                                            key={it}
                                                            className={`minute-value-item minute-value-item-${it} ${(this.GetRenderStore().minutes == it ? "time-value-item-selected" : "")} ${minutesEnabled.includes(it) == false ? "disabled" : ""}`}
                                                            onClick={_ => this.TimeMinutesDropDownValueSelect(it)}
                                                        >
                                                            {it.toString().padStart(2, "0")}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            ]
                    }
                </div>
            </div >
        );
    }
}

export default observer(DateTimePicker);